import { useState } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import { Box, Button, Modal, Stack, Tooltip } from '@mui/material';

// Utils
import { DateTimeFormat, parseError } from '../../../utils';

// Redux
import { SettingsState } from '../../../redux/slices/settingsSlice';

// Types
import { getValueOrDefault } from '../../../types/Device';
import { PairingInfo } from '../../../types/PairingInfo';
import { DeviceEnriched } from '../../../types/DeviceEnriched';

// Services
import { updateIsDeviceHealthyOverride } from '../../../services/device.service';
import { getAllSettingsByScope } from '../../../services/setting.service';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { getCurrentEnvironment } from '../../../helpers/determine-env';

// Components
import DisplayLabels from '../labels/DisplayLabels';
import { OverviewCard, OverviewGrid, OverviewInline, GridField } from '../../common/OverviewCard/OverviewCard';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';
import AuthService from '../../../services/auth.service';

interface DeviceStageProp {
  device: DeviceEnriched;
  pairing?: PairingInfo;
  settings?: SettingsState;
}

export default function DeviceStage(props: DeviceStageProp) {
  const { device, pairing } = props;
  const { notifyMessage } = useNotifyMessage();

  const [isHealthModalOpen, setIsHealthModalOpen] = useState(false);
  const [overrideHealth, setOverrideHealth] = useState<boolean | null>(null);

  const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;

  const currentEnvironment = getCurrentEnvironment();
  const canOverrideHealth = currentEnvironment === 'local' || currentEnvironment === 'dev' || currentEnvironment === 'preprod';

  const handleOpenHealthModal = () => {
    setIsHealthModalOpen(true);
    getAllSettingsByScope('DEVICE').then((res) => {
      const overrideSettingValue = res.find((x) => x.name === 'IsHealthyOverrideMap')?.value;
      if (overrideSettingValue) {
        const map = JSON.parse(overrideSettingValue);
        setOverrideHealth(map[device.deviceId] ?? null);
      }
    });
  };
  const handleCloseHealthModal = () => setIsHealthModalOpen(false);

  const handleSaveHealthOverride = async () => {
    try {
      const res = await updateIsDeviceHealthyOverride(device.deviceId, overrideHealth);
      Object.assign(device, res);
      handleCloseHealthModal();
      location.reload();
    } catch (err) {
      notifyMessage(parseError(err), 'error');
    }
  };

  const renderPairingValidation = (status: number) => {
    if (status === 0) return <HistoryOutlinedIcon style={{ color: 'grey', fontSize: '1.2rem' }} />;
    if (status === 1) return <CheckBoxOutlinedIcon style={{ color: 'green', fontSize: '1.2rem' }} />;
    if (status === 2) return <LockIcon style={{ color: 'red', fontSize: '1.2rem' }} />;
    return null;
  };

  const pairedToValue = (
    <OverviewInline>
      <span>{getValueOrDefault(pairing?.equipment?.serialNumber)}</span>
      {pairing?.currentPairing?.pairingValidationStatus && (
        <Tooltip title={pairing?.currentPairing?.pairingValidationStatusDescr} disableInteractive>
          <span>{renderPairingValidation(pairing.currentPairing.pairingValidationStatus)}</span>
        </Tooltip>
      )}
    </OverviewInline>
  );

  const healthLabel = device.isDeviceHealthy == null ? 'N/A' : device.isDeviceHealthy ? 'Healthy' : 'Unhealthy';
  const healthClass =
    device.isDeviceHealthy == null ? 'overview-value--neutral' : device.isDeviceHealthy ? 'overview-value--ok' : 'overview-value--bad';

  const healthValue = (
    <OverviewInline>
      <span className={healthClass}>{healthLabel}</span>
      {canOverrideHealth && !isExternalUser && (
        <button type='button' className='overview-edit-btn' onClick={handleOpenHealthModal}>
          Edit
        </button>
      )}
    </OverviewInline>
  );

  const rows: GridField[][] = [
    [
      { label: 'Device Type', value: getValueOrDefault(device.type?.type) },
      { label: 'Owner', value: getValueOrDefault(device.owner?.name) },
      { label: 'Type Name', value: getValueOrDefault(device.type?.name) },
    ],
    [
      // Date-only fields stay pinned to UTC (the `true, true` args): with no time shown,
      // rendering them in local time would silently shift the calendar day.
      ...(!isExternalUser
        ? [
            {
              label: 'Manufacturer Date (UTC)',
              value: DateTimeFormat.toStandardDate(device.manufactureDateTime, true, true),
            },
            {
              label: 'Manufacturer',
              value: getValueOrDefault(device.type?.manufacturer),
            },
            {
              label: 'Purchase Date (UTC)',
              value: DateTimeFormat.toStandardDate(device.purchaseDateTime, true, true),
            },
            {
              label: 'Purchase Order',
              value: getValueOrDefault(device.purchaseOrder),
            },
          ]
        : []),
    ],
    [
      ...(!isExternalUser
        ? [
            { label: 'Paired To', value: pairedToValue },
            {
              label: 'Paired Since (UTC)',
              value: DateTimeFormat.toStandardDate(pairing?.currentPairing?.pairedDateTime, true, true),
            },
            {
              label: 'Paired By',
              value: getValueOrDefault(pairing?.currentPairing?.pairedUserName),
            },
            {
              label: 'Shipment Number',
              value: getValueOrDefault(device.shipmentNbr),
            },
          ]
        : []),
    ],
    [
      { label: 'Device Health', value: healthValue },
      ...(!isExternalUser
        ? [
            { label: 'Secondary Device ID', value: getValueOrDefault(device.secondaryDeviceId) },
            {
              label: 'Device Labels',
              value: (
                <Stack direction='row' spacing={1}>
                  <DisplayLabels labels={getValueOrDefault(device?.labels)} />
                </Stack>
              ),
            },
          ]
        : []),
    ],
  ];

  return (
    <>
      <OverviewCard title='Device Details'>
        <OverviewGrid columns={4} rows={rows} />
      </OverviewCard>

      <Modal open={isHealthModalOpen} onClose={handleCloseHealthModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            minWidth: 300,
          }}
        >
          <h3>Override Device Health</h3>
          <p>Only use this for debugging purposes.</p>
          <p>Find a list of all overridden devices at Settings / IsHealthyOverrideMap</p>
          <Stack spacing={2}>
            <label>
              Override Health:&nbsp;
              <select
                value={overrideHealth === null ? 'clear' : overrideHealth ? 'true' : 'false'}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'clear') setOverrideHealth(null);
                  else setOverrideHealth(val === 'true');
                }}
              >
                <option value='clear'>No Override</option>
                <option value='true'>Force True</option>
                <option value='false'>Force False</option>
              </select>
            </label>
            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Button onClick={handleCloseHealthModal}>Cancel</Button>
              <Button onClick={handleSaveHealthOverride} variant='contained' size='small' sx={tablePrimaryButtonSx}>
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </>
  );
}
